import { isAxiosError } from 'axios';
import { getLogger } from 'loglevel';

import {
	NotAuthenticatedError,
	NotAuthorizedError,
	ServerError,
	ServerProblemError,
	ServerValidationError,
} from '@apple/utils/api/errors';
import type { ApiErrorResponse, HttpProblemDetails } from '@apple/utils/api/models';
import {
	isApiErrorResponse,
	isGenericErrorResponse,
	isHttpProblemDetails,
} from '@apple/utils/api/models';

import type { FieldErrors, FormErrors } from './models';
import { isAdditionalDataErrorResponse, isModelStateErrorResponse } from './models';

const log = getLogger('validation:server');

/**
 * @see Manzanita.Web\Infrastructure\Http\ApiControllerExtensions.cs
 * @see Arvato.eCoSystem.Web\Http\Extensions\ControllerExtensions.cs
 * @remark Known Error Responses:
 * ```
 * 400: Bad Request:                additionalData = undefined | ModelValidationResponse
 * 404: Not Found:                  additionalData = undefined | ApiErrorResponse<FieldErrors>
 * 406: Not Acceptable:             additionalData = undefined | ModelValidationResponse
 * 409: Conflict:                   additionalData = undefined | ApiErrorResponse<FieldErrors>
 * 410: Gone:                       additionalData = undefined
 * 417: Expectation Failed:         additionalData = undefined | ApiErrorResponse<FieldErrors>
 * 422: Unprocessable Entity:       additionalData = undefined | ModelValidationResponse
 * 500: Internal Server Error:      additionalData = undefined | ApiErrorResponse<ErrorMessages>
 * ```
 */
export function parseServerError<T>(error: unknown): ServerError | ServerValidationError<T> {
	if (error instanceof ServerError || error instanceof ServerValidationError) {
		return error;
	}

	if (!(isAxiosError<unknown, unknown>(error) && error.response)) {
		log.error('An unknown error occurred while making the request:', error);
		return new ServerError('An unknown error occurred while making the request.', {
			innerError: error,
		});
	}

	if (error.response.status === 401) {
		log.warn('User is not authenticated (401)', error);
		return new NotAuthenticatedError(error);
	}

	if (error.response.status === 403) {
		log.warn('User is not authorized (403)', error);
		return new NotAuthorizedError(error);
	}

	if (typeof error.response.data === 'string') {
		// For plain text responses
		return new ServerError(error.response.data, error);
	}

	if (isModelStateErrorResponse(error.response.data)) {
		return new ServerValidationError(error, {
			// see tag uris: https://datatracker.ietf.org/doc/html/rfc4151
			type: 'tag:apple.arvato.com:validation',
			title: 'Validation Error',
			detail: 'The request was invalid.',
			status: error.response.status,
			instance: error.response.config.url,
			formErrors: undefined,
			fieldErrors: error.response.data.modelState,
		});
	}

	if (isAdditionalDataErrorResponse(error.response.data)) {
		return new ServerValidationError(error, {
			// see tag uris: https://datatracker.ietf.org/doc/html/rfc4151
			type: 'tag:apple.arvato.com:validation',
			title: 'Validation Error',
			detail: 'The request was invalid.',
			status: error.response.status,
			instance: error.response.config.url,
			formErrors: undefined,
			fieldErrors: error.response.data.additionalData,
		});
	}

	if (isApiErrorResponse(error.response.data)) {
		const apiErrorResponse = error.response.data as ApiErrorResponse<
			FormErrors | FieldErrors<T>
		>;

		return new ServerValidationError(error, {
			// see tag uris: https://datatracker.ietf.org/doc/html/rfc4151
			type: 'tag:apple.arvato.com:validation',
			title: apiErrorResponse.titleKey,
			detail: apiErrorResponse.contentKey,
			status: error.response.status,
			instance: error.response.config.url,
			formErrors: Array.isArray(apiErrorResponse.additionalData)
				? apiErrorResponse.additionalData
				: undefined,
			fieldErrors: !Array.isArray(apiErrorResponse.additionalData)
				? (apiErrorResponse.additionalData ?? undefined)
				: undefined,
		});
	}

	if (isGenericErrorResponse(error.response.data)) {
		return new ServerError(error.response.data.message, error);
	}

	if (isHttpProblemDetails(error.response.data)) {
		const problemDetails = error.response.data as HttpProblemDetails & {
			errors?: Record<string, string[] | string>;
			fieldErrors?: FieldErrors<T>;
			formErrors?: FormErrors;
		};

		if (
			'fieldErrors' in problemDetails ||
			'formErrors' in problemDetails ||
			'errors' in problemDetails
		) {
			return new ServerValidationError(error, {
				type: problemDetails.type,
				title: problemDetails.title,
				status: problemDetails.status,
				detail: problemDetails.detail ?? problemDetails.title,
				instance: problemDetails.instance,
				formErrors: problemDetails.formErrors,
				fieldErrors:
					problemDetails.fieldErrors ??
					(problemDetails.errors as FieldErrors<T>) ??
					undefined,
			});
		}

		return new ServerProblemError(error, problemDetails);
	}

	return new ServerError('An unknown error occurred.', error);
}
